// MaintenanceHistoryModal.tsx
import React from "react";
import {
  Box,
  Text,
  VStack,
  HStack,
  Badge,
  Button,
  Dialog,
  Table,
} from "@chakra-ui/react";

// 정비 내역 타입 정의
interface MaintenanceRecord {
  id: string;
  equipmentId: number;
  equipmentName: string;
  date: string;
  code: string;
  description: string;
  cost: number;
  partsUsed: string;
  partsDuration: string;
  technician: string;
  priority: "high" | "medium" | "low";
  status: "scheduled" | "in-progress" | "completed";
  estimatedHours: number;
  actualHours?: number;
}

interface MaintenanceHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  records: MaintenanceRecord[];
  onDeleteRecord?: (id: string) => void;
  onUpdateStatus?: (
    id: string,
    status: "scheduled" | "in-progress" | "completed"
  ) => void;
}

// 색상 팔레트
const colorPalette = {
  PM: "#3182ce", // 파란색 - Preventive Maintenance
  CM: "#e53e3e", // 빨간색 - Corrective Maintenance
  PV: "#38a169", // 초록색 - Predictive Maintenance
  UP: "#805ad5", // 보라색 - Upgrade
  CL: "#dd6b20", // 주황색 - Cleaning
  IN: "#d69e2e", // 노란색 - Inspection
} as const;

type ColorPaletteKey = keyof typeof colorPalette;

// 첫 두 글자로 컬러 코드를 가져오는 함수
const getColorByPrefix = (value: string) => {
  const prefix = value.substring(0, 2).toUpperCase() as ColorPaletteKey;
  return colorPalette[prefix] || "#718096"; // 기본 회색
};

// 우선순위 색상 가져오기
const getPriorityColor = (priority: string) => {
  switch (priority) {
    case "high":
      return "red";
    case "medium":
      return "orange";
    case "low":
      return "green";
    default:
      return "gray";
  }
};

// 우선순위 텍스트 가져오기
const getPriorityText = (priority: string) => {
  switch (priority) {
    case "high":
      return "높음";
    case "medium":
      return "보통";
    case "low":
      return "낮음";
    default:
      return "알 수 없음";
  }
};

// RecordTable 컴포넌트 (외부 정의)
interface RecordTableProps {
  records: MaintenanceRecord[];
  title: string;
  statusColor: string;
  onStatusChange: (
    recordId: string,
    newStatus: "scheduled" | "in-progress" | "completed"
  ) => void;
  onDelete: (recordId: string) => void;
}

const RecordTable: React.FC<RecordTableProps> = ({
  records,
  title,
  statusColor,
  onStatusChange,
  onDelete,
}) => (
  <VStack gap={4} align="stretch">
    <HStack>
      <Badge colorScheme={statusColor} size="lg" px={3} py={1}>
        {title} ({records.length}건)
      </Badge>
    </HStack>

    {records.length > 0 ? (
      <Box overflowX="auto">
        <Table.Root variant="outline" size="sm">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader minW="100px">날짜</Table.ColumnHeader>
              <Table.ColumnHeader minW="150px">장비명</Table.ColumnHeader>
              <Table.ColumnHeader minW="100px">코드</Table.ColumnHeader>
              <Table.ColumnHeader minW="200px">작업 내용</Table.ColumnHeader>
              <Table.ColumnHeader minW="80px">비용</Table.ColumnHeader>
              <Table.ColumnHeader minW="120px">사용 부품</Table.ColumnHeader>
              <Table.ColumnHeader minW="80px">부품 기간</Table.ColumnHeader>
              <Table.ColumnHeader minW="80px">담당자</Table.ColumnHeader>
              <Table.ColumnHeader minW="80px">우선순위</Table.ColumnHeader>
              <Table.ColumnHeader minW="100px">예상 시간</Table.ColumnHeader>
              <Table.ColumnHeader minW="120px">작업</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {records.map((record) => (
              <Table.Row key={record.id}>
                <Table.Cell>{record.date}</Table.Cell>
                <Table.Cell>
                  <Text fontSize="sm" fontWeight="medium">
                    {record.equipmentName}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <HStack>
                    <Box
                      w={3}
                      h={3}
                      bg={getColorByPrefix(record.code)}
                      borderRadius="full"
                    />
                    <Badge
                      variant="outline"
                      borderColor={getColorByPrefix(record.code)}
                      color={getColorByPrefix(record.code)}
                      size="sm"
                    >
                      {record.code}
                    </Badge>
                  </HStack>
                </Table.Cell>
                <Table.Cell maxW="200px">
                  <Text
                    fontSize="sm"
                    title={record.description}
                    style={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {record.description}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <Text fontSize="sm" fontWeight="bold">
                    ₩{record.cost}만
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <Text fontSize="sm">{record.partsUsed || "-"}</Text>
                </Table.Cell>
                <Table.Cell>
                  <Text fontSize="sm">{record.partsDuration || "-"}</Text>
                </Table.Cell>
                <Table.Cell>
                  <Text fontSize="sm">{record.technician || "-"}</Text>
                </Table.Cell>
                <Table.Cell>
                  <Badge
                    colorScheme={getPriorityColor(record.priority)}
                    size="sm"
                  >
                    {getPriorityText(record.priority)}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <Text fontSize="sm">
                    {record.estimatedHours}시간
                    {record.actualHours && (
                      <Text as="span" color="gray.500" ml={1}>
                        (실제: {record.actualHours}시간)
                      </Text>
                    )}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <VStack gap={1} align="stretch">
                    {record.status === "scheduled" && (
                      <Button
                        size="xs"
                        colorScheme="blue"
                        variant="outline"
                        onClick={() => onStatusChange(record.id, "in-progress")}
                      >
                        시작
                      </Button>
                    )}
                    {record.status === "in-progress" && (
                      <Button
                        size="xs"
                        colorScheme="green"
                        variant="outline"
                        onClick={() => onStatusChange(record.id, "completed")}
                      >
                        완료
                      </Button>
                    )}
                    <Button
                      size="xs"
                      colorScheme="red"
                      variant="ghost"
                      onClick={() => onDelete(record.id)}
                    >
                      삭제
                    </Button>
                  </VStack>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Box>
    ) : (
      <Box textAlign="center" py={4} bg="gray.50" borderRadius="md">
        <Text color="gray.500" fontSize="sm">
          {title} 내역이 없습니다.
        </Text>
      </Box>
    )}
  </VStack>
);

const MaintenanceHistoryModal: React.FC<MaintenanceHistoryModalProps> = ({
  isOpen,
  onClose,
  records,
  onDeleteRecord,
  onUpdateStatus,
}) => {
  const handleStatusChange = (
    recordId: string,
    newStatus: "scheduled" | "in-progress" | "completed"
  ) => {
    if (onUpdateStatus) {
      onUpdateStatus(recordId, newStatus);
    }
  };

  const handleDelete = (recordId: string) => {
    if (onDeleteRecord && confirm("정말로 이 정비 내역을 삭제하시겠습니까?")) {
      onDeleteRecord(recordId);
    }
  };

  // 상태별로 레코드 분류
  const scheduledRecords = records.filter((r) => r.status === "scheduled");
  const inProgressRecords = records.filter((r) => r.status === "in-progress");
  const completedRecords = records.filter((r) => r.status === "completed");

  return (
    <Dialog.Root open={isOpen} onOpenChange={(e) => !e.open && onClose()}>
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content maxW="8xl" mx={4} h="90vh">
          <Dialog.Header>
            <Dialog.Title>정비 내역 관리 (총 {records.length}건)</Dialog.Title>
            <Dialog.CloseTrigger />
          </Dialog.Header>

          <Dialog.Body overflowY="auto">
            <VStack gap={8} align="stretch">
              {/* 요약 통계 */}
              <HStack gap={6} p={4} bg="gray.50" borderRadius="lg">
                <VStack>
                  <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                    {scheduledRecords.length}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    예정
                  </Text>
                </VStack>
                <VStack>
                  <Text fontSize="2xl" fontWeight="bold" color="yellow.600">
                    {inProgressRecords.length}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    진행중
                  </Text>
                </VStack>
                <VStack>
                  <Text fontSize="2xl" fontWeight="bold" color="green.600">
                    {completedRecords.length}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    완료
                  </Text>
                </VStack>
                <VStack>
                  <Text fontSize="2xl" fontWeight="bold" color="gray.800">
                    ₩{records.reduce((sum, r) => sum + r.cost, 0)}만
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    총 비용
                  </Text>
                </VStack>
              </HStack>

              {/* 예정된 정비 */}
              <RecordTable
                records={scheduledRecords}
                title="예정된 정비"
                statusColor="blue"
                onStatusChange={handleStatusChange}
                onDelete={handleDelete}
              />

              {/* 진행중인 정비 */}
              <RecordTable
                records={inProgressRecords}
                title="진행중인 정비"
                statusColor="yellow"
                onStatusChange={handleStatusChange}
                onDelete={handleDelete}
              />

              {/* 완료된 정비 */}
              <RecordTable
                records={completedRecords}
                title="완료된 정비"
                statusColor="green"
                onStatusChange={handleStatusChange}
                onDelete={handleDelete}
              />

              {records.length === 0 && (
                <Box textAlign="center" py={12}>
                  <Text color="gray.500" fontSize="lg">
                    등록된 정비 내역이 없습니다.
                  </Text>
                  <Text color="gray.400" fontSize="sm" mt={2}>
                    예지보전 스케줄에서 정비 일정을 등록해보세요.
                  </Text>
                </Box>
              )}
            </VStack>
          </Dialog.Body>

          <Dialog.Footer>
            <HStack justify="space-between" w="100%">
              <Text fontSize="sm" color="gray.500">
                💡 상태를 변경하거나 불필요한 내역을 삭제할 수 있습니다.
              </Text>
              <Button onClick={onClose}>닫기</Button>
            </HStack>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
};

export default MaintenanceHistoryModal;
